import { useState, useCallback } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { usePaymentsContext } from '../../shared/providers/PaymentsProvider';
import { CheckoutSessionParams } from '../../shared/types';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId, validateEmail } from '../../shared/utils/validation';

interface UseCheckoutOptions {
  onSuccess?: (sessionId: string) => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
}

interface UseCheckoutReturn {
  loading: boolean;
  error: string | null;
  // Backward compatibility getters
  isLoading: boolean; // Maps to loading
  isError: boolean; // Maps to !!error
  createCheckoutSession: (
    params: CheckoutSessionParams
  ) => Promise<string | null>;
  redirectToCheckout: (params: CheckoutSessionParams) => Promise<boolean>;
  createPaymentIntent: (params: {
    amount: number;
    currency: string;
    customerId?: string;
    description?: string;
    metadata?: Record<string, string>;
  }) => Promise<string | null>;
}

export const useCheckout = (
  options: UseCheckoutOptions = {}
): UseCheckoutReturn => {
  const { onSuccess, onError, onLoading } = options;
  const stripe = useStripe();
  const { initialized } = usePaymentsContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      onError?.(errorMessage);
      console.error('Checkout error:', errorMessage);
    },
    [onError]
  );

  const validateCheckoutParams = useCallback(
    (params: CheckoutSessionParams): string | null => {
      if (!params.priceId) {
        return 'Price ID is required';
      }

      if (!validateStripeId(params.priceId, 'price')) {
        return ERROR_MESSAGES.INVALID_PRICE_ID;
      }

      if (
        params.customerId &&
        !validateStripeId(params.customerId, 'customer')
      ) {
        return ERROR_MESSAGES.INVALID_CUSTOMER_ID;
      }

      if (params.customerEmail && !validateEmail(params.customerEmail)) {
        return 'Invalid customer email';
      }

      if (!params.successUrl) {
        return 'Success URL is required';
      }

      if (!params.cancelUrl) {
        return 'Cancel URL is required';
      }

      return null;
    },
    []
  );

  const createCheckoutSession = useCallback(
    async (params: CheckoutSessionParams): Promise<string | null> => {
      if (!initialized) {
        handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
        return null;
      }

      const validationError = validateCheckoutParams(params);
      if (validationError) {
        handleError(validationError);
        return null;
      }

      setLoading(true);
      setError(null);
      onLoading?.(true);

      try {
        const response = await fetch('/api/payments/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || ERROR_MESSAGES.CHECKOUT_FAILED);
        }

        const { sessionId } = await response.json();

        if (!sessionId) {
          throw new Error('No session ID returned from server');
        }

        onSuccess?.(sessionId);
        return sessionId;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : ERROR_MESSAGES.CHECKOUT_FAILED;
        handleError(errorMessage);
        return null;
      } finally {
        setLoading(false);
        onLoading?.(false);
      }
    },
    [initialized, validateCheckoutParams, handleError, onSuccess, onLoading]
  );

  const redirectToCheckout = useCallback(
    async (params: CheckoutSessionParams): Promise<boolean> => {
      if (!stripe) {
        handleError(ERROR_MESSAGES.STRIPE_NOT_LOADED);
        return false;
      }

      const sessionId = await createCheckoutSession(params);

      if (!sessionId) {
        return false;
      }

      try {
        const result = await stripe.redirectToCheckout({ sessionId });

        if (result.error) {
          throw new Error(
            result.error.message || ERROR_MESSAGES.CHECKOUT_FAILED
          );
        }

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : ERROR_MESSAGES.CHECKOUT_FAILED;
        handleError(errorMessage);
        return false;
      }
    },
    [stripe, createCheckoutSession, handleError]
  );

  const createPaymentIntent = useCallback(
    async (params: {
      amount: number;
      currency: string;
      customerId?: string;
      description?: string;
      metadata?: Record<string, string>;
    }): Promise<string | null> => {
      if (!initialized) {
        handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
        return null;
      }

      if (params.amount <= 0) {
        handleError('Amount must be greater than 0');
        return null;
      }

      if (
        params.customerId &&
        !validateStripeId(params.customerId, 'customer')
      ) {
        handleError(ERROR_MESSAGES.INVALID_CUSTOMER_ID);
        return null;
      }

      setLoading(true);
      setError(null);
      onLoading?.(true);

      try {
        const response = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || 'Failed to create payment intent');
        }

        const { client_secret } = await response.json();

        if (!client_secret) {
          throw new Error('No client secret returned from server');
        }

        return client_secret;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to create payment intent';
        handleError(errorMessage);
        return null;
      } finally {
        setLoading(false);
        onLoading?.(false);
      }
    },
    [initialized, handleError, onLoading]
  );

  return {
    loading,
    error,
    // Backward compatibility getters
    isLoading: loading,
    isError: !!error,
    createCheckoutSession,
    redirectToCheckout,
    createPaymentIntent,
  };
};
